import React, { useState } from "react";

import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { TicketApiError } from "../services/ticketsApi";
import { setBoxStacked } from "../services/boxesApi";

interface StackedButtonProps {
  scuName: string;
  isStacked: boolean;
  /** Provenance — when set we know an MTTR operator (or 'ops via workcenter')
   *  marked this. Surfaced in the tooltip. */
  setBy?: string | null;
  /** Optional: callback after a successful flip, so the parent can
   *  refresh the snapshot or show a toast. */
  onChanged?: (newIsStacked: boolean) => void;
}

/**
 * Small "Mark Stacked / Unstack" toggle. Confirms before posting since
 * stacking has cascade effects (closes outage, resolves linked tickets).
 *
 * Posts to /api/boxes/{scu}/stacked via boxesApi. Optimistic — flips
 * the visible state on click and rolls back on error.
 */
export const StackedButton: React.FC<StackedButtonProps> = ({
  scuName,
  isStacked,
  setBy,
  onChanged,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const by = (user?.display_name ?? "").trim();

  const onClick = async (): Promise<void> => {
    if (!by) {
      setError("Your login has no display name.");
      return;
    }
    const targetStacked = !isStacked;
    if (targetStacked) {
      if (
        !window.confirm(
          `Mark ${scuName} as Stacked?\n\n` +
            "This will:\n" +
            "  • close any open outage on this box\n" +
            "  • resolve linked _jcox tickets (overlay for source tickets)\n" +
            "  • stop auto-tickets from firing until you un-stack\n\n" +
            "Use this when the box is intentionally offline " +
            "(in storage, between jobs, etc.).",
        )
      ) {
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      await setBoxStacked(scuName, targetStacked, by);
      onChanged?.(targetStacked);
    } catch (e) {
      const msg =
        e instanceof TicketApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "unknown error";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-xs space-y-1">
      {isStacked ? (
        <div
          className="inline-block px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-800 font-semibold uppercase tracking-wide"
          title={setBy ? `Stacked by ${setBy}` : "Stacked (source workcenter)"}
        >
          STACKED
        </div>
      ) : null}
      {error && (
        <div className="text-error-700 bg-error-50 px-1.5 py-0.5 rounded">
          {error}
        </div>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => void onClick()}
        className="h-6 px-2 text-xs"
      >
        {busy
          ? "…"
          : isStacked
            ? "Unstack"
            : "Mark Stacked"}
      </Button>
    </div>
  );
};

export default StackedButton;
